import { useContext, useEffect } from "react";
import { Helmet } from "react-helmet";
import SearchContext from "../Provider/SearchProvider/index";

const SITE_URL = "https://app.zuresmile.com";
const OG_IMAGE = `${SITE_URL}/og-image.png`;
const SITE_NAME = "ZureSmile";
const BRAND_SUFFIX = "| ZureSmile Sri Lanka";

/**
 * Enhanced Meta component with dynamic doctor/clinic metadata,
 * rich SEO fallbacks, canonical URLs, og:image, structured data, and
 * strict sanitization against "undefined".
 */
const Meta = ({ slug, title: customTitle, description: customDescription, type, doctor, clinic }) => {
  const { seoData, getSeoData } = useContext(SearchContext) || {};

  useEffect(() => {
    if (!seoData || seoData.length === 0) {
      if (typeof getSeoData === "function") getSeoData();
    }
  }, [seoData, getSeoData]);

  // Clean string helper ensuring no "undefined" or "null" appears
  const cleanStr = (str, fallback = "") => {
    if (!str || typeof str !== "string") return fallback;
    const trimmed = str.trim();
    if (
      trimmed === "undefined" ||
      trimmed === "null" ||
      trimmed === "NaN" ||
      trimmed === ""
    ) {
      return fallback;
    }
    return trimmed
      .replace(/\bundefined\b/gi, "")
      .replace(/\bnull\b/gi, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  // Rich page metadata dictionary — each entry has title, description, keywords, canonical, schema
  const defaultPageMeta = {
    home: {
      title: `ZureSmile Clear Aligners | Certified Invisible Orthodontics in Sri Lanka`,
      description:
        "Transform your smile with ZureSmile clear aligners. Discreet, comfortable, and custom-designed by certified orthodontic specialists across Sri Lanka for fast, predictable results.",
      keywords:
        "clear aligners Sri Lanka, invisible braces, teeth aligners, ZureSmile, smile transformation, orthodontic treatment",
      canonical: `${SITE_URL}/`,
      schema: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
        description: "Certified clear aligner treatments across Sri Lanka.",
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    },
    doctors: {
      title: `Find Certified Clear Aligner Doctors & Orthodontic Specialists ${BRAND_SUFFIX}`,
      description:
        "Browse certified ZureSmile orthodontic specialists and dental providers across Sri Lanka. View qualifications, ratings, and book a personalized clear aligner consultation.",
      keywords:
        "orthodontic specialists Sri Lanka, clear aligner doctors, dental specialists, ZureSmile doctors, certified orthodontists",
      canonical: `${SITE_URL}/doctor`,
      schema: {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "ZureSmile Certified Orthodontic Specialists",
        description: "Directory of certified ZureSmile orthodontic specialists across Sri Lanka.",
        url: `${SITE_URL}/doctor`,
      },
    },
    clinic: {
      title: `Certified Dental Clinics & Clear Aligner Treatment Centers ${BRAND_SUFFIX}`,
      description:
        "Find authorized ZureSmile partner dental clinics across Sri Lanka. Locate centers near you for 3D digital smile evaluations, clear aligner fittings, and expert orthodontic care.",
      keywords:
        "dental clinics Sri Lanka, ZureSmile clinics, clear aligner centers, orthodontic treatment centers, partner dental clinics",
      canonical: `${SITE_URL}/clinic`,
      schema: {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "ZureSmile Authorized Partner Dental Clinics",
        description: "Directory of ZureSmile authorized partner clinics across Sri Lanka.",
        url: `${SITE_URL}/clinic`,
      },
    },
    search: {
      title: `Search Clear Aligner Clinics & Orthodontic Doctors Near You ${BRAND_SUFFIX}`,
      description:
        "Search top-rated ZureSmile partner dental clinics and certified aligner specialists near you. Compare verified reviews and book your in-person consultation today.",
      keywords:
        "clear aligner clinics near me, orthodontic doctors near me, ZureSmile search, dental clinics search, teeth aligner providers",
      canonical: `${SITE_URL}/search`,
      schema: null,
    },
    thankyou: {
      title: `Consultation Request Confirmed — Thank You ${BRAND_SUFFIX}`,
      description:
        "Your consultation request has been successfully submitted to ZureSmile. Our certified partner dental clinic will contact you shortly to confirm your appointment details.",
      keywords: "appointment confirmed, ZureSmile consultation, dental booking confirmation",
      canonical: `${SITE_URL}/thankyou`,
      schema: null,
    },
    notfound: {
      title: `Page Not Found (404) ${BRAND_SUFFIX}`,
      description:
        "The page you are looking for could not be found. Explore ZureSmile clear aligner treatments, find a certified clinic, or return to our home page.",
      keywords: "ZureSmile 404, page not found",
      canonical: `${SITE_URL}/`,
      schema: null,
    },
  };

  let resolvedTitle = "";
  let resolvedDescription = "";
  let resolvedKeywords = "";
  let resolvedCanonical = "";
  let resolvedSchema = null;

  // 1. Direct custom title / description overrides
  if (customTitle) {
    resolvedTitle = cleanStr(customTitle);
  }
  if (customDescription) {
    resolvedDescription = cleanStr(customDescription);
  }

  // 2. Doctor profile dynamic metadata
  if (!resolvedTitle && (type === "doctor" || doctor)) {
    const rawDocName = doctor?.name || doctor?.doctor_details?.name || "";
    const cleanDocName = cleanStr(rawDocName.replace(/^Dr\.?\s*/i, ""), "Orthodontic Specialist");
    const spec = cleanStr(
      doctor?.specialization || doctor?.doctor_details?.specialization,
      "Clear Aligner Specialist"
    );
    const clinicName = cleanStr(
      doctor?.clinic_name || doctor?.clinic?.name,
      "ZureSmile Partner Clinic"
    );

    resolvedTitle = `Dr. ${cleanDocName} — Certified ${spec} at ${clinicName} ${BRAND_SUFFIX}`;
    resolvedDescription = `Book a consultation with Dr. ${cleanDocName}, a certified ${spec} at ${clinicName}. Specializing in ZureSmile invisible aligners, 3D smile assessments, and comprehensive orthodontic treatment in Sri Lanka.`;
    resolvedKeywords = `Dr. ${cleanDocName}, ${spec}, ${clinicName}, clear aligner specialist, ZureSmile doctor`;
    resolvedCanonical = `${SITE_URL}/doctor/${slug || ""}`;
    resolvedSchema = {
      "@context": "https://schema.org",
      "@type": "Physician",
      name: `Dr. ${cleanDocName}`,
      medicalSpecialty: spec,
      worksFor: { "@type": "MedicalOrganization", name: clinicName },
      url: resolvedCanonical,
      description: resolvedDescription,
    };
  }

  // 3. Clinic profile dynamic metadata
  if (!resolvedTitle && (type === "clinic" || clinic)) {
    const clinicObj = Array.isArray(clinic) ? clinic[0] : clinic;
    const rawClinicName = clinicObj?.name || "";
    const cleanClinicName = cleanStr(rawClinicName, "ZureSmile Partner Dental Clinic");
    const city = cleanStr(
      clinicObj?.city ||
        clinicObj?.district_data?.name ||
        clinicObj?.district_name ||
        clinicObj?.address_line2,
      "Sri Lanka"
    );
    const province = cleanStr(clinicObj?.province_data?.name || clinicObj?.province_name, "");
    const locationStr = province ? `${city}, ${province}` : city;
    const address = cleanStr(clinicObj?.address_line1, "");

    resolvedTitle = `${cleanClinicName} — Authorized ZureSmile Partner Clinic in ${city} ${BRAND_SUFFIX}`;
    resolvedDescription = `Visit ${cleanClinicName} in ${locationStr} for certified ZureSmile clear aligners, 3D digital smile evaluations, and professional dental consultations with trained orthodontic specialists.${address ? ` Located at ${address}.` : ""}`;
    resolvedKeywords = `${cleanClinicName}, dental clinic ${city}, clear aligner clinic ${city}, ZureSmile partner clinic, orthodontic treatment ${locationStr}`;
    resolvedCanonical = `${SITE_URL}/clinic/${slug || ""}`;
    resolvedSchema = {
      "@context": "https://schema.org",
      "@type": "Dentist",
      name: cleanClinicName,
      address: {
        "@type": "PostalAddress",
        addressLocality: city,
        addressRegion: province,
        addressCountry: "LK",
        streetAddress: address,
      },
      url: resolvedCanonical,
      description: resolvedDescription,
      medicalSpecialty: "Orthodontics",
    };
  }

  // 4. Match backend dynamic seoData database records by slug
  if (!resolvedTitle && slug) {
    const dbMeta = seoData?.find((item) => item?.slug === slug);
    if (dbMeta?.meta_title) {
      resolvedTitle = cleanStr(dbMeta.meta_title);
    }
    if (dbMeta?.meta_description) {
      resolvedDescription = cleanStr(dbMeta.meta_description);
    }
  }

  // 5. Match pre-defined page dictionary fallbacks
  if (!resolvedTitle && slug && defaultPageMeta[slug]) {
    const page = defaultPageMeta[slug];
    resolvedTitle = page.title;
    if (!resolvedDescription) resolvedDescription = page.description;
    if (!resolvedKeywords) resolvedKeywords = page.keywords;
    if (!resolvedCanonical) resolvedCanonical = page.canonical;
    if (!resolvedSchema) resolvedSchema = page.schema;
  }

  // 6. Absolute global fallback
  if (!resolvedTitle)
    resolvedTitle = `ZureSmile — Advanced Clear Aligners & Invisible Orthodontics in Sri Lanka`;
  if (!resolvedDescription)
    resolvedDescription =
      "Transform your smile with ZureSmile clear aligners. Discreet, comfortable, doctor-led orthodontic treatments designed for your lifestyle across Sri Lanka.";
  if (!resolvedKeywords)
    resolvedKeywords = "ZureSmile, clear aligners, invisible braces, orthodontics Sri Lanka";
  if (!resolvedCanonical) resolvedCanonical = `${SITE_URL}/`;

  // Final sanitization
  resolvedTitle = cleanStr(resolvedTitle, "ZureSmile — Clear Aligners Sri Lanka");
  resolvedDescription = cleanStr(resolvedDescription, "ZureSmile doctor-led clear aligners across Sri Lanka.");

  // 404 should never be indexed
  const robotsContent = slug === "notfound" ? "noindex, nofollow" : "index, follow";

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="keywords" content={resolvedKeywords} />
      <meta name="robots" content={robotsContent} />
      <meta name="author" content={SITE_NAME} />
      <link rel="canonical" href={resolvedCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === "doctor" || type === "clinic" ? "profile" : "website"} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_LK" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@zuresmile" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* JSON-LD Structured Data */}
      {resolvedSchema && (
        <script type="application/ld+json">
          {JSON.stringify(resolvedSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default Meta;
